import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { debounce } from 'lodash';
import styled, { createGlobalStyle } from 'styled-components';
import { Checkbox as AntdCheckbox } from 'antd';
import {
  SearchOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';
import {
  Button,
  Modal,
  Radio,
  RadioGroup,
  Tag,
  Input,
  Select,
  SelectOption,
  Pagination,
} from 'components';
import LockboxStatusTag from 'components/LockboxStatusTag';
import { Loading } from '@app/react-common';
import errorReporter from '@app/errorReporter';
import { getSupraLockboxData } from 'api/SupraLockboxController';
import { AuthContextUser } from 'contexts';
import {
  isGlobalAdmin as checkIsGlobalAdmin,
  isAgent as checkIsAgent,
  isBranchAdmin as checkIsBranchAdmin,
  isTeamAdmin as checkIsTeamAdmin,
  isOwner as checkIsOwner,
  isManager as checkIsManager,
} from '@bb/users';
import type { IBranchAdminUser } from '@bb/users';
import SearchLabel from '../../Router/pages/QuickLockboxAssignmentPage/SearchLabel';

type PermissionMode = 'owner' | 'assignee' | 'shackle';
type AssignMode = 'assign' | 'unassign';
type ModalStep = 'selection' | 'review' | 'confirmation';

interface LockboxRow {
  id: string;
  serialNumber: string;
  boxType: string;
  owner: string | null;
  assignee: string | null;
  assigneeId: string | null;
  shacklePermissions: number | null;
  status: string;
}

interface ConfirmationResult {
  successful: number;
  unsuccessful: number;
  failedLockboxes?: LockboxRow[];
}

type OwnerAssigneeSearchResult = {
  id?: string;
  type?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  officeName?: string;
  oid?: string;
  branchName?: string;
  address?: {
    line1?: string;
  };
};

type TargetSelection = {
  id: string;
  type: 'user' | 'office';
  label: string;
};

export interface LockboxPermissionsManagerModalProps {
  visible: boolean;
  loggedInUser?: AuthContextUser;
  onConfirmAndProcess?: (payload: {
    mode: PermissionMode;
    assignMode: AssignMode;
    searchValue: string;
    ownerOrAssigneeType: string;
    target: TargetSelection | null;
    targetId: string;
    targetType: 'user' | 'office' | '';
    targetLabel: string;
    assigneeId?: string;
    assigneeType?: 'user' | 'office' | '';
    selectedIds: string[];
    selectedSerialNumbers: string[];
    lockboxAssigneeMap?: Record<string, string>;
    grantOfficeShacklePermissions?: boolean;
  }) => Promise<ConfirmationResult> | void;
  onCancel?: () => void;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'New', label: 'New' },
  { value: 'Used', label: 'Used' },
  { value: 'Assigned', label: 'Assigned' },
];

const AGENT_TYPE_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'office', label: 'Office' },
];

const MODE_LABELS: Record<PermissionMode, string> = {
  owner: 'Change Owner',
  assignee: 'Change Assignee',
  shackle: 'Change Shackle Permissions',
};

const FETCHED_PAGE_SIZE = 5;

type UserRole =
  | 'staffAdmin'
  | 'globalAdmin'
  | 'branchAdmin'
  | 'teamAdmin'
  | 'agent';

const resolveUserRole = (user: AuthContextUser): UserRole | undefined => {
  const isStaff = user.org?.type === 'mlsStaff';
  const isAdminLevel =
    checkIsGlobalAdmin(user) || checkIsOwner(user) || checkIsManager(user);

  if (isStaff && isAdminLevel) return 'staffAdmin';
  if (isAdminLevel) return 'globalAdmin';
  if (checkIsBranchAdmin(user)) return 'branchAdmin';
  if (checkIsTeamAdmin(user)) return 'teamAdmin';
  if (checkIsAgent(user)) return 'agent';
  return undefined;
};

const buildFetchParams = (
  loggedInUser: AuthContextUser | undefined,
  page: number,
  statusFilter?: string,
  searchText?: string,
  requireOwnerAsMLS?: boolean,
  permissionMode?: PermissionMode,
) => {
  if (!loggedInUser) return undefined;

  const role = resolveUserRole(loggedInUser);
  const lockboxStatuses = statusFilter
    ? [statusFilter]
    : ['New', 'Used', 'Assigned'];
  const commonParams = {
    lockboxStatuses,
    ...(searchText && { searchText }),
    ...(requireOwnerAsMLS && { requireOwnerAsMLS }),
    page,
  };

  switch (role) {
    case 'staffAdmin':
      return {
        mlsStaffOrgId: loggedInUser.org.id,
        filterBySupraOwnerMapping: true,
        ...commonParams,
      };
    case 'globalAdmin':
      return { ownerOrgId: loggedInUser.org.id, ...commonParams };
    case 'branchAdmin':
      return {
        ownerBranchOrgId: loggedInUser.org.id,
        branchLocations:
          (loggedInUser as any as IBranchAdminUser).locations ||
          [(loggedInUser as any as IBranchAdminUser).location].filter(Boolean),
        ...commonParams,
      };
    case 'teamAdmin':
    case 'agent':
      return {
        ownerId: loggedInUser.id ?? '',
        ownerType: 'user' as const,
        ...(permissionMode === 'shackle' && {
          assigneeId: loggedInUser.id ?? '',
          assigneeType: 'user' as const,
        }),
        ...commonParams,
      };
    default:
      return undefined;
  }
};

export const LockboxPermissionsManagerModal: React.FC<
  LockboxPermissionsManagerModalProps
> = ({ visible, loggedInUser, onConfirmAndProcess, onCancel }) => {
  // Wizard step
  const [step, setStep] = useState<ModalStep>('selection');

  // Selection step state
  const [permissionMode, setPermissionMode] = useState<PermissionMode>('owner');
  const [assignMode, setAssignMode] = useState<AssignMode>('assign');
  const [ownerOrAssigneeType, setOwnerOrAssigneeType] =
    useState<string>('user');
  const [grantOfficeShacklePermissions, setGrantOfficeShacklePermissions] =
    useState<boolean>(false);
  const [searchTarget, setSearchTarget] = useState<string>('');
  const [searchTargetLabel, setSearchTargetLabel] = useState<string>('');
  const [targetSelection, setTargetSelection] =
    useState<TargetSelection | null>(null);
  const [searchResults, setSearchResults] = useState<
    OwnerAssigneeSearchResult[]
  >([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [lockboxSearch, setLockboxSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Fetched lockboxes for Change Owner mode
  const [fetchedLockboxes, setFetchedLockboxes] = useState<LockboxRow[]>([]);
  const [fetchedTotal, setFetchedTotal] = useState(0);
  const [lockboxesLoading, setLockboxesLoading] = useState(false);
  const [allFetchedRowsById, setAllFetchedRowsById] = useState<
    Record<string, LockboxRow>
  >({});
  // Incrementing this forces a lockbox refetch even if other deps haven't changed
  const [lockboxFetchTrigger, setLockboxFetchTrigger] = useState(0);

  // Review step state
  const [reviewIds, setReviewIds] = useState<string[]>([]);

  // Confirmation step state
  const [confirmationResult, setConfirmationResult] =
    useState<ConfirmationResult | null>(null);
  const [processing, setProcessing] = useState(false);

  const mapRawToRow = (lb: any): LockboxRow => ({
    id: String(lb._id),
    serialNumber: String(lb.serialNumber ?? ''),
    boxType: [lb.productCode, lb.serialNumber].filter(Boolean).join(' '),
    owner:
      lb.mlsOwnerName ||
      lb.branchOwnerName ||
      lb.orgOwnerName ||
      lb.officeOwnerName ||
      (lb.ownerType === 'user'
        ? [(lb.owner as any)?.firstName, (lb.owner as any)?.lastName]
            .filter(Boolean)
            .join(' ') || null
        : null),
    assignee:
      lb.assigneeType === 'user'
        ? [(lb.assignee as any)?.firstName, (lb.assignee as any)?.lastName]
            .filter(Boolean)
            .join(' ') || null
        : null,
    assigneeId:
      lb.assigneeType === 'user'
        ? (lb.assignee as any)?._id || null
        : lb.assigneeType === 'branch'
          ? lb.assignee
            ? `${lb.assignee}-${lb.assigneeBranchIndex || ''}`
            : null
          : lb.assignee
            ? String(lb.assignee)
            : null,
    shacklePermissions: (lb.granteeUserIds?.length ?? null) as number | null,
    status: lb.lockboxStatus || 'Unknown',
  });

  const fetchLockboxesForOwner = useCallback(
    async (
      page: number,
      status?: string,
      searchText?: string,
      requireMLS?: boolean,
      mode?: PermissionMode,
    ) => {
      setLockboxesLoading(true);
      try {
        const params = buildFetchParams(
          loggedInUser,
          page,
          status,
          searchText,
          requireMLS,
          mode,
        );
        const { lockboxes: raw, lockboxCount } =
          await getSupraLockboxData(params);
        const rows = raw.map(mapRawToRow);
        setFetchedLockboxes(rows);
        setFetchedTotal(lockboxCount);
        // Merge into accumulated map so rows from all visited pages are available
        setAllFetchedRowsById(prev => {
          const next = { ...prev };
          rows.forEach(row => {
            next[row.id] = row;
          });
          return next;
        });
      } catch (err) {
        setFetchedLockboxes([]);
        setFetchedTotal(0);
        errorReporter.error(err);
      } finally {
        setLockboxesLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedInUser],
  );

  // Always use API-fetched lockboxes for all permission modes
  const activeSource = fetchedLockboxes;

  const displayedLockboxes = useMemo(() => activeSource, [activeSource]);

  const reviewLockboxes = useMemo(
    () =>
      reviewIds
        .map(
          id => allFetchedRowsById[id] ?? activeSource.find(lb => lb.id === id),
        )
        .filter((lb): lb is LockboxRow => !!lb),
    [reviewIds, allFetchedRowsById, activeSource],
  );

  const allDisplayedSelected =
    displayedLockboxes.length > 0 &&
    displayedLockboxes.every(lb => selectedIds.has(lb.id));

  const someSelected = selectedIds.size > 0;

  const isAssigneeMode = permissionMode === 'assignee';

  const currentFieldLabel = isAssigneeMode
    ? 'Current Assignee'
    : permissionMode === 'owner'
      ? 'Current Owner'
      : 'Current Permission';

  const newFieldLabel = isAssigneeMode
    ? 'New Assignee'
    : permissionMode === 'owner'
      ? 'New Owner'
      : 'New Permission';

  const resolvedTargetName =
    assignMode === 'unassign' ? 'Unassigned' : searchTargetLabel || '—';

  const hasErrors = confirmationResult && confirmationResult.unsuccessful > 0;
  const allSuccess =
    confirmationResult && confirmationResult.unsuccessful === 0;

  const getSearchResultDisplayName = (item: OwnerAssigneeSearchResult) => {
    if (item.type === 'office') {
      const officeName = item.officeName || item.name || 'Office';
      return item.oid ? `${officeName} #${item.oid}` : officeName;
    }

    const userName =
      item.name ||
      [item.firstName, item.lastName].filter(Boolean).join(' ').trim();

    return userName || item.id || 'Unknown';
  };

  const handleTargetSearch = useCallback(
    async (term: string) => {
      if (!term) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const queryString =
          `searchTerm=${encodeURIComponent(term)}` +
          '&includeUserMlsId=1&includeOfficeId=1&includeOfficeName=1&size=10&page=0' +
          `&type=${ownerOrAssigneeType}`;
        const {
          data: { searchResult = [] },
        } = await axios.get(`/lockbox/owner_and_assignee?${queryString}`);
        setSearchResults(searchResult);
      } catch (err) {
        setSearchResults([]);
        errorReporter.error(err);
      } finally {
        setSearchLoading(false);
      }
    },
    [ownerOrAssigneeType],
  );

  const debouncedTargetSearch = useMemo(
    () => debounce(handleTargetSearch, 300),
    [handleTargetSearch],
  );

  useEffect(
    () => () => {
      debouncedTargetSearch.cancel();
    },
    [debouncedTargetSearch],
  );

  // Fetch lockboxes on modal open, on mode change, on page change,
  // on statusFilter/lockboxSearch change, or when lockboxFetchTrigger increments.
  const isAssigneeOfficeMode =
    permissionMode === 'assignee' && ownerOrAssigneeType === 'office';

  useEffect(() => {
    if (!visible) return;
    fetchLockboxesForOwner(
      currentPage,
      statusFilter,
      lockboxSearch || undefined,
      isAssigneeOfficeMode,
      permissionMode,
    );
  }, [
    visible,
    permissionMode,
    ownerOrAssigneeType,
    currentPage,
    fetchLockboxesForOwner,
    lockboxFetchTrigger,
    statusFilter,
    lockboxSearch,
    isAssigneeOfficeMode,
  ]);

  // Reset to page 1 when status filter or search text changes to avoid stale pagination
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, lockboxSearch]);

  const isUnassignMode = isAssigneeMode && assignMode === 'unassign';

  const isRowDisabled = (lb: LockboxRow) => isUnassignMode && !lb.assigneeId;

  const toggleRow = (id: string) => {
    const lb = displayedLockboxes.find(l => l.id === id);
    if (lb && isRowDisabled(lb)) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = () =>
    setSelectedIds(
      new Set(activeSource.filter(lb => !isRowDisabled(lb)).map(lb => lb.id)),
    );

  const handleSelectPage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      displayedLockboxes
        .filter(lb => !isRowDisabled(lb))
        .forEach(lb => next.add(lb.id));
      return next;
    });
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleHeaderCheckbox = () => {
    if (allDisplayedSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        displayedLockboxes.forEach(lb => next.delete(lb.id));
        return next;
      });
    } else {
      handleSelectPage();
    }
  };

  const handleGoToReview = () => {
    setReviewIds(Array.from(selectedIds));
    setStep('review');
  };

  const handleRemoveFromReview = (id: string) => {
    setReviewIds(prev => prev.filter(rid => rid !== id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleBackToSelection = () => setStep('selection');

  const handleConfirmAndProcess = async () => {
    const targetType = (targetSelection?.type || '') as 'user' | 'office' | '';
    const targetId = targetSelection?.id || '';
    const targetLabel = targetSelection?.label || '';

    setProcessing(true);
    try {
      // Build per-lockbox assignee map for unassign mode
      const lockboxAssigneeMap: Record<string, string> = {};
      if (isAssigneeMode && assignMode === 'unassign') {
        reviewIds.forEach(id => {
          const lb =
            allFetchedRowsById[id] ?? activeSource.find(l => l.id === id);
          if (lb?.assigneeId && lb.serialNumber) {
            lockboxAssigneeMap[lb.serialNumber] = lb.assigneeId;
          }
        });
      }

      const result = await onConfirmAndProcess?.({
        mode: permissionMode,
        assignMode,
        searchValue: targetId,
        ownerOrAssigneeType: targetType || ownerOrAssigneeType,
        target: targetSelection,
        targetId,
        targetType,
        targetLabel,
        assigneeId: permissionMode === 'assignee' ? targetId : undefined,
        assigneeType: permissionMode === 'assignee' ? targetType : undefined,
        selectedIds: reviewIds,
        selectedSerialNumbers: reviewIds
          .map(
            id =>
              (allFetchedRowsById[id] ?? activeSource.find(lb => lb.id === id))
                ?.serialNumber ?? '',
          )
          .filter(Boolean),
        lockboxAssigneeMap,
        grantOfficeShacklePermissions:
          permissionMode === 'shackle' &&
          ownerOrAssigneeType === 'user' &&
          grantOfficeShacklePermissions,
      });

      setConfirmationResult(
        result || { successful: reviewIds.length, unsuccessful: 0 },
      );
      setStep('confirmation');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    resetState();
    onCancel?.();
  };

  // "Assign more lockboxes" — reset form/target state and force a lockbox
  // refetch so the table is up-to-date after the previous assignment.
  const handleStartNew = () => {
    resetUserSelection();
    setLockboxFetchTrigger(prev => prev + 1);
  };

  // Resets only form / target / selection state. Fetched lockboxes are kept
  // in memory so they don't need to be re-fetched on every mode switch.
  const resetUserSelection = () => {
    setStep('selection');
    setPermissionMode('owner');
    setAssignMode('assign');
    setOwnerOrAssigneeType('user');
    setGrantOfficeShacklePermissions(false);
    setSearchTarget('');
    setSearchTargetLabel('');
    setTargetSelection(null);
    setSearchResults([]);
    setSearchLoading(false);
    setLockboxSearch('');
    setStatusFilter('');
    setSelectedIds(new Set());
    setCurrentPage(1);
    setReviewIds([]);
    setConfirmationResult(null);
  };

  // Full reset including cached lockbox data — called only on modal close.
  const resetState = () => {
    resetUserSelection();
    setFetchedLockboxes([]);
    setFetchedTotal(0);
    setAllFetchedRowsById({});
    setLockboxesLoading(false);
    setLockboxFetchTrigger(0);
  };

  const renderSelectionStep = () => (
    <>
      {/* Permission mode */}
      <StyledSection>
        <SectionLabel>Change ownership, assignment or permissions</SectionLabel>
        <StyledRadioGroup
          fullWidth
          value={permissionMode}
          onChange={e => {
            setPermissionMode(e.target.value as PermissionMode);
            setGrantOfficeShacklePermissions(false);
            setSearchTarget('');
            setSearchTargetLabel('');
            setTargetSelection(null);
            setSearchResults([]);
            setSelectedIds(new Set());
            setLockboxSearch('');
            setStatusFilter('');
            setCurrentPage(1);
          }}
        >
          <StyledRadioCard value="owner">Change Owner</StyledRadioCard>
          <StyledRadioCard value="assignee">Change Assignee</StyledRadioCard>
          <StyledRadioCard value="shackle">
            Change Shackle Permissions
          </StyledRadioCard>
        </StyledRadioGroup>

        {/* Assign or Unassign (only for assignee mode) */}
        {isAssigneeMode && (
          <>
            <SectionLabel>Assign or Unassign</SectionLabel>
            <StyledRadioGroup
              fullWidth
              value={assignMode}
              onChange={e => setAssignMode(e.target.value as AssignMode)}
            >
              <StyledRadioCard value="assign">Assign</StyledRadioCard>
              <StyledRadioCard value="unassign">Unassign</StyledRadioCard>
            </StyledRadioGroup>
          </>
        )}

        {/* Search row — hidden for agents in unassign mode (auto-targets self) */}
        {!(isAssigneeMode && assignMode === 'unassign') && (
          <StyledSearchRow>
            <Select
              value={ownerOrAssigneeType}
              className={'agent-type-select'}
              onChange={(val: string) => {
                setOwnerOrAssigneeType(val);
                setGrantOfficeShacklePermissions(false);
                setSearchTarget('');
                setSearchTargetLabel('');
                setTargetSelection(null);
                setSearchResults([]);
              }}
              style={{ width: 110 }}
            >
              {AGENT_TYPE_OPTIONS.map(opt => (
                <SelectOption key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectOption>
              ))}
            </Select>
            <Select
              showSearch
              filterOption={false}
              showArrow={false}
              allowClear
              optionLabelProp="title"
              value={searchTarget || undefined}
              placeholder="Search for a user or office"
              getPopupContainer={trigger =>
                trigger.closest('.ant-modal-body') || document.body
              }
              onSearch={debouncedTargetSearch}
              onChange={(val?: string) => {
                const selectedValue = val || '';
                setSearchTarget(selectedValue);
                if (!selectedValue) {
                  setSearchTargetLabel('');
                  setTargetSelection(null);
                  return;
                }

                const selected = searchResults.find(
                  item => `${item.id}|${item.type}` === selectedValue,
                );
                if (
                  selected?.id &&
                  (selected.type === 'user' || selected.type === 'office')
                ) {
                  const label = getSearchResultDisplayName(selected);
                  setSearchTargetLabel(label);
                  setTargetSelection({
                    id: selected.id,
                    type: selected.type,
                    label,
                  });
                  return;
                }

                const [rawId, rawType] = selectedValue.split('|');
                if (rawId && (rawType === 'user' || rawType === 'office')) {
                  setSearchTargetLabel(rawId);
                  setTargetSelection({
                    id: rawId,
                    type: rawType,
                    label: rawId,
                  });
                } else {
                  setSearchTargetLabel('');
                  setTargetSelection(null);
                }
              }}
              notFoundContent={searchLoading ? <Loading /> : null}
              style={{ flex: 1 }}
            >
              {searchResults.map(item => (
                <SelectOption
                  key={`${item.id}|${item.type}`}
                  value={`${item.id}|${item.type}`}
                  title={getSearchResultDisplayName(item)}
                >
                  {SearchLabel(item as any, true)}
                </SelectOption>
              ))}
            </Select>
          </StyledSearchRow>
        )}

        {permissionMode === 'shackle' && ownerOrAssigneeType === 'user' && (
          <ShackleCheckboxWrapper>
            <AntdCheckbox
              checked={grantOfficeShacklePermissions}
              onChange={e => setGrantOfficeShacklePermissions(e.target.checked)}
            >
              Grant Shackle permissions to everyone in office
            </AntdCheckbox>
          </ShackleCheckboxWrapper>
        )}
      </StyledSection>

      <StyledDivider />

      {/* Lockbox selection table */}
      <StyledSection>
        <SectionLabel>
          Select Lockboxes (
          {someSelected ? `${selectedIds.size} Selected` : '0 Selected'})
        </SectionLabel>

        <StyledToolbar>
          <Input
            prefix={<SearchOutlined />}
            suffix={
              lockboxSearch ? (
                <CloseOutlined
                  style={{ cursor: 'pointer', color: '#aaa' }}
                  onClick={() => setLockboxSearch('')}
                />
              ) : null
            }
            placeholder="Search by serial number"
            value={lockboxSearch}
            onChange={e => setLockboxSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            value={statusFilter}
            onChange={(val: string) => setStatusFilter(val)}
            style={{ width: 150 }}
          >
            {STATUS_OPTIONS.map(opt => (
              <SelectOption key={opt.value} value={opt.value}>
                {opt.label}
              </SelectOption>
            ))}
          </Select>
        </StyledToolbar>

        <StyledBulkActions>
          <Button size="small" onClick={handleSelectAll}>
            Select All ({fetchedTotal.toLocaleString()})
          </Button>
          <Button size="small" onClick={handleSelectPage}>
            Select Page ({displayedLockboxes.length})
          </Button>
          <Button
            size="small"
            onClick={handleClearSelection}
            disabled={selectedIds.size <= 0}
          >
            Clear Selection
          </Button>
        </StyledBulkActions>
        <StyledTable>
          <StyledThead>
            <tr>
              <StyledTh style={{ width: 40 }}>
                <AntdCheckbox
                  checked={allDisplayedSelected}
                  indeterminate={!allDisplayedSelected && someSelected}
                  onChange={handleHeaderCheckbox}
                />
              </StyledTh>
              <StyledTh>Box Type</StyledTh>
              <StyledTh>Owner</StyledTh>
              <StyledTh>Assignee</StyledTh>
              <StyledTh>Shackle Permissions</StyledTh>
              <StyledTh>Status</StyledTh>
            </tr>
          </StyledThead>
          <tbody>
            {lockboxesLoading ? (
              <tr>
                <StyledTd
                  colSpan={6}
                  style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: 'rgba(0,0,0,0.45)',
                  }}
                >
                  <Loading size="fa-3x" />
                </StyledTd>
              </tr>
            ) : displayedLockboxes.length === 0 ? (
              <tr>
                <StyledTd
                  colSpan={6}
                  style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: 'rgba(0,0,0,0.45)',
                  }}
                >
                  No lockboxes found
                </StyledTd>
              </tr>
            ) : (
              displayedLockboxes.map(lb => {
                const disabled = isRowDisabled(lb);
                return (
                  <StyledTr
                    key={lb.id}
                    $selected={selectedIds.has(lb.id)}
                    onClick={() => !disabled && toggleRow(lb.id)}
                    style={
                      disabled
                        ? { opacity: 0.5, cursor: 'not-allowed' }
                        : undefined
                    }
                    title={
                      disabled
                        ? 'This lockbox has no assignee to unassign'
                        : undefined
                    }
                  >
                    <StyledTd onClick={e => e.stopPropagation()}>
                      <AntdCheckbox
                        checked={selectedIds.has(lb.id)}
                        onChange={() => toggleRow(lb.id)}
                        disabled={disabled}
                      />
                    </StyledTd>
                    <StyledTd>{lb.boxType}</StyledTd>
                    <StyledTd>
                      {lb.owner ?? <Tag color="default">Unassigned</Tag>}
                    </StyledTd>
                    <StyledTd>
                      {lb.assignee ?? <Tag color="default">Unassigned</Tag>}
                    </StyledTd>
                    <StyledTd>
                      {lb.shacklePermissions !== null ? (
                        <Tag color="green">{lb.shacklePermissions} users</Tag>
                      ) : (
                        <span style={{ color: '#8c8c8c' }}>None</span>
                      )}
                    </StyledTd>
                    <StyledTd>
                      <LockboxStatusTag lockboxStatus={lb.status as any} />
                    </StyledTd>
                  </StyledTr>
                );
              })
            )}
          </tbody>
        </StyledTable>

        <StyledPaginationRow>
          <PaginationLabel>
            {fetchedTotal.toLocaleString()} lockboxes found
          </PaginationLabel>
          <Pagination
            current={currentPage}
            total={fetchedTotal}
            pageSize={FETCHED_PAGE_SIZE}
            onChange={page => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </StyledPaginationRow>
      </StyledSection>
    </>
  );

  const renderReviewStep = () => (
    <>
      <ReviewDescription>
        Review and confirm the changes you&apos;re about to make
      </ReviewDescription>

      {/* Change Summary cards */}
      <StyledSection>
        <SectionLabel>Change Summary</SectionLabel>
        <SummaryCardsRow>
          <SummaryCard $variant="green">
            <SummaryCardLabel>Action</SummaryCardLabel>
            <SummaryCardValue>{MODE_LABELS[permissionMode]}</SummaryCardValue>
          </SummaryCard>
          <SummaryCard $variant="white">
            <SummaryCardLabel>
              {assignMode === 'unassign' ? 'Change to' : 'Assign to'}
            </SummaryCardLabel>
            <SummaryCardValue>{resolvedTargetName}</SummaryCardValue>
          </SummaryCard>
          <SummaryCard $variant="white">
            <SummaryCardLabel>Lockboxes affected</SummaryCardLabel>
            <SummaryCardValue>{reviewIds.length} lockboxes</SummaryCardValue>
          </SummaryCard>
        </SummaryCardsRow>
      </StyledSection>

      <StyledDivider />

      {/* Lockbox review table */}
      <StyledSection>
        <SectionLabel>Lockboxes</SectionLabel>
        <StyledTable>
          <StyledThead>
            <tr>
              <StyledTh>Box Type</StyledTh>
              <StyledTh>{currentFieldLabel}</StyledTh>
              <StyledTh>{newFieldLabel}</StyledTh>
              <StyledTh style={{ width: 40 }} />
            </tr>
          </StyledThead>
          <tbody>
            {reviewLockboxes.map(lb => {
              const currentValue = isAssigneeMode
                ? lb.assignee
                : permissionMode === 'owner'
                  ? lb.owner
                  : lb.shacklePermissions !== null
                    ? `${lb.shacklePermissions} users`
                    : null;

              return (
                <ReviewTr key={lb.id}>
                  <StyledTd>{lb.boxType}</StyledTd>
                  <StyledTd>
                    {currentValue ?? <Tag color="default">Unassigned</Tag>}
                  </StyledTd>
                  <StyledTd>
                    {assignMode === 'unassign' ? (
                      <span style={{ color: '#f5222d' }}>Unassigned</span>
                    ) : (
                      <span style={{ color: '#52c41a' }}>
                        {searchTargetLabel || '—'}
                      </span>
                    )}
                  </StyledTd>
                  <StyledTd>
                    <RemoveButton onClick={() => handleRemoveFromReview(lb.id)}>
                      <CloseOutlined />
                    </RemoveButton>
                  </StyledTd>
                </ReviewTr>
              );
            })}
          </tbody>
        </StyledTable>
      </StyledSection>
    </>
  );

  const renderConfirmationStep = () => {
    if (!confirmationResult) return null;
    const totalProcessed =
      confirmationResult.successful + confirmationResult.unsuccessful;

    return (
      <>
        {/* Success / partial icon + message */}
        <ConfirmationHeader>
          {allSuccess ? (
            <CheckCircleFilled style={{ fontSize: 48, color: '#1890ff' }} />
          ) : (
            <CheckCircleFilled style={{ fontSize: 48, color: '#faad14' }} />
          )}
          <ConfirmationTitle>
            {allSuccess
              ? 'All changes applied successfully!'
              : 'Changes applied with some errors'}
          </ConfirmationTitle>
          {hasErrors && (
            <ConfirmationSubtitle>
              {confirmationResult.successful}/{totalProcessed} lockboxes were
              processed successfully.
            </ConfirmationSubtitle>
          )}
        </ConfirmationHeader>

        {/* Summary section */}
        <StyledSection>
          <SectionLabel>Summary</SectionLabel>
          <SummaryCardsRow>
            <SummaryCountCard $variant="green">
              <CheckCircleFilled style={{ color: '#52c41a', marginRight: 8 }} />
              <div>
                <SummaryCardLabel>Successful</SummaryCardLabel>
                <SummaryCardValue>
                  {confirmationResult.successful}
                </SummaryCardValue>
              </div>
            </SummaryCountCard>
            {hasErrors && (
              <SummaryCountCard $variant="red">
                <CloseCircleFilled
                  style={{ color: '#f5222d', marginRight: 8 }}
                />
                <div>
                  <SummaryCardLabel>Unsuccessful</SummaryCardLabel>
                  <SummaryCardValue>
                    {confirmationResult.unsuccessful}
                  </SummaryCardValue>
                </div>
              </SummaryCountCard>
            )}
          </SummaryCardsRow>
        </StyledSection>

        <StyledDivider />

        {/* Action Details */}
        <StyledSection>
          <SectionLabel>Action Details</SectionLabel>
          <ActionDetailsRow>
            <ActionDetailItem>
              <ActionDetailLabel>Action</ActionDetailLabel>
              <ActionDetailValue>
                {MODE_LABELS[permissionMode]}
              </ActionDetailValue>
            </ActionDetailItem>
            <ActionDetailItem>
              <ActionDetailLabel>Mode</ActionDetailLabel>
              <ActionDetailValue style={{ textTransform: 'capitalize' }}>
                {assignMode}
              </ActionDetailValue>
            </ActionDetailItem>
            <ActionDetailItem>
              <ActionDetailLabel>
                {assignMode === 'unassign' ? 'Change to' : 'Assign to'}
              </ActionDetailLabel>
              <ActionDetailValue>{resolvedTargetName}</ActionDetailValue>
            </ActionDetailItem>
          </ActionDetailsRow>
        </StyledSection>

        {/* Processed lockboxes detail table */}
        {reviewLockboxes.length > 0 && (
          <>
            <StyledDivider />
            <StyledSection>
              <SectionLabel>
                Processed Lockboxes ({reviewLockboxes.length})
              </SectionLabel>
              <StyledTable>
                <StyledThead>
                  <tr>
                    <StyledTh>Serial Number</StyledTh>
                    <StyledTh>{currentFieldLabel}</StyledTh>
                    <StyledTh>{newFieldLabel}</StyledTh>
                  </tr>
                </StyledThead>
                <tbody>
                  {reviewLockboxes.map(lb => (
                    <ReviewTr key={lb.id}>
                      <StyledTd>{lb.serialNumber}</StyledTd>
                      <StyledTd>
                        {(isAssigneeMode
                          ? lb.assignee
                          : permissionMode === 'owner'
                            ? lb.owner
                            : lb.shacklePermissions !== null
                              ? `${lb.shacklePermissions} users`
                              : null) ?? <Tag color="default">Unassigned</Tag>}
                      </StyledTd>
                      <StyledTd>
                        {assignMode === 'unassign' ? (
                          <span style={{ color: '#f5222d' }}>Unassigned</span>
                        ) : (
                          <span style={{ color: '#52c41a' }}>
                            {searchTargetLabel || '—'}
                          </span>
                        )}
                      </StyledTd>
                    </ReviewTr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledSection>
          </>
        )}

        {/* Unsuccessful lockboxes table */}
        {hasErrors && confirmationResult.failedLockboxes && (
          <>
            <StyledDivider />
            <StyledSection>
              <SectionLabel>
                Unsuccessful lockboxes ({confirmationResult.unsuccessful})
              </SectionLabel>
              <StyledTable>
                <StyledThead>
                  <tr>
                    <StyledTh>Box Type</StyledTh>
                    <StyledTh>{currentFieldLabel}</StyledTh>
                    <StyledTh>{newFieldLabel}</StyledTh>
                  </tr>
                </StyledThead>
                <tbody>
                  {confirmationResult.failedLockboxes.map(lb => (
                    <ReviewTr key={lb.id}>
                      <StyledTd>{lb.boxType}</StyledTd>
                      <StyledTd>
                        {lb.assignee ?? <Tag color="default">Unassigned</Tag>}
                      </StyledTd>
                      <StyledTd>
                        <Tag color="red">Error</Tag>
                      </StyledTd>
                    </ReviewTr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledSection>
          </>
        )}
      </>
    );
  };

  const renderFooter = () => {
    switch (step) {
      case 'selection':
        return (
          <StyledFooter>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleGoToReview}
              disabled={
                !someSelected ||
                (!targetSelection &&
                  !(isAssigneeMode && assignMode === 'unassign'))
              }
            >
              Next
            </Button>
          </StyledFooter>
        );
      case 'review':
        return (
          <StyledFooter>
            <Button onClick={handleBackToSelection} disabled={processing}>
              Back
            </Button>
            <Button
              type="primary"
              onClick={handleConfirmAndProcess}
              disabled={reviewIds.length === 0 || processing}
              loading={processing}
            >
              Confirm and Process
            </Button>
          </StyledFooter>
        );
      case 'confirmation':
        return (
          <StyledFooter>
            <Button onClick={handleClose}>Close</Button>
            <Button type="primary" onClick={handleStartNew}>
              {hasErrors ? 'Assign more lockboxes' : 'Start new assignment'}
            </Button>
          </StyledFooter>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <LockboxPermissionsManagerModalStyles />
      <Modal
        title="Lockbox Permissions Manager"
        open={visible}
        onCancel={handleClose}
        width={760}
        footer={renderFooter()}
        destroyOnClose
        wrapClassName="lockbox-permissions-manager-modal"
      >
        {step === 'selection' && renderSelectionStep()}
        {step === 'review' && renderReviewStep()}
        {step === 'confirmation' && renderConfirmationStep()}
      </Modal>
    </>
  );
};

const LockboxPermissionsManagerModalStyles = createGlobalStyle`
  .lockbox-permissions-manager-modal .ant-modal {
    top: 2rem;
    padding: 4rem 0;
  }

  .lockbox-permissions-manager-modal .ant-modal-content {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 4rem);
    border-radius: 12px;
    overflow: hidden;
  }

  .lockbox-permissions-manager-modal .ant-modal-header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .lockbox-permissions-manager-modal .ant-modal-title {
    font-size: 16px;
    font-weight: 600;
  }

  .lockbox-permissions-manager-modal .ant-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .lockbox-permissions-manager-modal .ant-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
  }

  /* Dropdown renders inside modal body */
  .lockbox-permissions-manager-modal .ant-select-dropdown {
    z-index: 1051;
  }
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionLabel = styled.p`
  margin: 0;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const StyledDivider = styled.hr`
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 20px 0;
`;

const StyledSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:focus-within {
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }

  /* LabelWrapper around the type-picker: don't grow */
  & > div:first-child {
    flex-shrink: 0;
  }

  /* LabelWrapper around the search Select: fill remaining space */
  & > div:last-child {
    flex: 1;
    min-width: 0;
    width: 100%;
  }

  /* Type picker (first Select) */
  .ant-select:first-child .ant-select-selector,
  .agent-type-select .ant-select-selector {
    border: none !important;
    border-right: 1px solid #f0f0f0 !important;
    border-radius: 0 !important;
    background-color: #fafafa !important;
    min-width: 90px;
    height: 40px !important;
    display: flex !important;
    align-items: center !important;
    .ant-select-selection-item {
      align-items: center;
    }
  }

  /* Search Select (second Select) */
  .ant-select:not(:first-child) {
    width: 100% !important;
  }

  .ant-select:not(:first-child) .ant-select-selector {
    border: none !important;
    border-radius: 0 !important;
    background-color: #fff !important;
    height: 40px !important;
    padding: 4px 12px !important;
  }

  .ant-select-selection-search-input {
    height: 32px !important;
  }

  .ant-input {
    border: none;
    box-shadow: none;
  }
`;

const ShackleCheckboxWrapper = styled.div`
  margin-top: -4px;
`;

const StyledToolbar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledBulkActions = styled.div`
  display: flex;
  gap: 8px;

  .ant-btn {
    padding: 0 15px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const StyledThead = styled.thead`
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
`;

const StyledTh = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
`;

const StyledTr = styled.tr<{ $selected: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#e6f4ff' : 'transparent')};
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: ${({ $selected }) => ($selected ? '#bae0ff' : '#fafafa')};
  }
`;

const ReviewTr = styled.tr`
  border-bottom: 1px solid #f0f0f0;
`;

const StyledTd = styled.td`
  padding: 10px 12px;
  vertical-align: middle;
`;

const StyledPaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const PaginationLabel = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  &&& {
    width: 100% !important;
  }

  .ant-radio-group {
    display: flex !important;
    gap: 0.75rem;
    width: 100% !important;
  }
`;

const StyledRadioCard = styled(Radio)`
  &&& {
    flex: 1;
    border: 1px solid #d9d9d9 !important;
    border-radius: 6px !important;
    padding: 0.5rem 1rem !important;
    display: flex !important;
    align-items: center;
    background: #fff !important;

    .ant-radio {
      top: 0 !important;
    }

    &.ant-radio-wrapper-checked {
      border-color: #1677ff !important;
      background: #e6f4ff !important;
    }
  }
`;

const ReviewDescription = styled.p`
  margin: 0 0 16px;
  color: rgba(0, 0, 0, 0.65);
`;

const SummaryCardsRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SummaryCard = styled.div<{ $variant: 'green' | 'white' }>`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid
    ${({ $variant }) => ($variant === 'green' ? '#b7eb8f' : '#f0f0f0')};
  background: ${({ $variant }) => ($variant === 'green' ? '#f6ffed' : '#fff')};
`;

const SummaryCardLabel = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
`;

const SummaryCardValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: rgba(0, 0, 0, 0.85);
  }
`;

const ConfirmationHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 24px;
`;

const ConfirmationTitle = styled.h3`
  margin: 12px 0 4px;
  font-weight: 600;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.85);
`;

const ConfirmationSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
`;

const SummaryCountCard = styled.div<{ $variant: 'green' | 'red' }>`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid
    ${({ $variant }) => ($variant === 'green' ? '#b7eb8f' : '#ffa39e')};
  background: ${({ $variant }) =>
    $variant === 'green' ? '#f6ffed' : '#fff1f0'};
`;

const ActionDetailsRow = styled.div`
  display: flex;
  gap: 32px;
`;

const ActionDetailItem = styled.div``;

const ActionDetailLabel = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
`;

const ActionDetailValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;
